import { LitElement, html, render } from "lit";

import { customElement, property, state } from "lit/decorators.js";

import { routerCss } from "./wow-router.css";
import { isProcessingCheckout } from "../process-stripe-checkout";

export type RoutePage<TPage = unknown> = LitElement & {
  canDeactivate?: (currentPage: TPage, targetRoute: string) => Promise<boolean> | boolean;
};

export interface RouteConfig<TPage extends RoutePage = LitElement> {
  page: new () => TPage;
  isPrivate?: boolean;
  requiredRole?: string;
  isDefault?: boolean;
  is404?: boolean;
  title?: string;
  layout?: new () => HTMLElement;
}

// Extract prefix type from pattern string
type ExtractPrefixType<T extends string> = T extends `/${infer Segment}/${infer Rest}`
  ? { [K in Segment]: string } & ExtractPrefixType<`/${Rest}`>
  : T extends `/${infer Segment}`
    ? { [K in Segment]: string }
    : Record<string, never>;

type RouterInstance<T extends Record<string, RouteConfig>, TPrefixPattern extends string = ""> = {
  navigateTo: WowRouter<T, TPrefixPattern>["navigateTo"];
  router: WowRouter<T, TPrefixPattern>;
};

if (window.location.hash === "#_=_") {
  // this hash can be added bu FB during oauth redirect, so we clean it
  window.location.hash = "";
}
if (window.location.hash === "#") {
  // this hash can be added bu FB during oauth redirect, so we clean it
  window.location.hash = "";
}

@customElement("wow-router")
export class WowRouter<
  TConfig extends Record<string, RouteConfig>,
  TPrefixPattern extends string = "",
> extends LitElement {
  @property({ type: Object })
  private config: TConfig;

  @state()
  public currentRoute!: keyof TConfig;

  public historyState: unknown;

  // Store the current page instance and its config for canDeactivate checks
  // Use a more specific type that preserves the page type information
  private _currentPageInstance: RoutePage | undefined;
  private _currentRouteConfig: TConfig[keyof TConfig] | undefined;

  // Public strongly-typed prefix object
  public readonly routePrefix: ExtractPrefixType<TPrefixPattern> = {} as ExtractPrefixType<TPrefixPattern>;

  // Parsed prefix segments from pattern
  private prefixSegments: string[] = [];

  public remainingSegments: string[] = [];

  static override styles = [routerCss];

  constructor(
    config: TConfig,
    private defaultLayout?: new () => HTMLElement,
    private prefixPathPattern: TPrefixPattern = "" as TPrefixPattern,
  ) {
    super();
    this.config = config;
    this.currentRoute = this.getDefaultRoute();
    this.handlePopState = this.handlePopState.bind(this);
    this.prefixSegments = this.parsePrefixPattern(prefixPathPattern);

    const baseTag = document.querySelector("base");
    if (baseTag) {
      this.baseUrl = baseTag.getAttribute("href") || "/";
    }

    this.setRouteFromUrl();
  }

  private getDefaultRoute(): keyof TConfig {
    if (Object.keys(this.config).length === 0) {
      throw new Error("Router config must contain at least one route");
    }
    const defaultRoute = Object.entries(this.config).find(([, config]) => config.isDefault)?.[0];
    const firstRoute = Object.keys(this.config)[0];
    if (!defaultRoute && !firstRoute) {
      throw new Error("Failed to determine default route");
    }
    return (defaultRoute || firstRoute) as keyof TConfig;
  }

  baseUrl = "/";

  private parsePrefixPattern(pattern: string): string[] {
    if (!pattern || pattern === "") return [];
    return pattern.split("/").filter((segment) => segment !== "");
  }

  private extractPrefixFromPath(pathname: string): {
    prefixValues: Record<string, string>;
    remainingPath: string;
    remainingSegments: string[];
  } | null {
    // First, remove baseUrl from pathname if present
    let processedPath = pathname;
    if (this.baseUrl !== "/") {
      const baseUrlPath = this.baseUrl
        .split("/")
        .filter((x) => x !== "")
        .join("/");
      if (baseUrlPath) {
        processedPath = processedPath.replace(new RegExp(`^/${baseUrlPath}`), "");
      }
    }

    if (this.prefixSegments.length === 0) {
      const pathSegments = processedPath.split("/").filter((segment) => segment !== "");
      return { prefixValues: {}, remainingPath: processedPath, remainingSegments: pathSegments };
    }

    const pathSegments = processedPath.split("/").filter((segment) => segment !== "");

    // Check if we have enough segments for the prefix
    if (pathSegments.length < this.prefixSegments.length) {
      return null; // Not enough segments - will trigger 404
    }

    // Extract prefix values
    const prefixValues: Record<string, string> = {};
    for (let i = 0; i < this.prefixSegments.length; i++) {
      const segment = this.prefixSegments[i];
      const pathValue = pathSegments[i];
      if (segment && pathValue) {
        prefixValues[segment] = pathValue;
      }
    }

    // Build remaining path
    const remainingSegments = pathSegments.slice(this.prefixSegments.length);
    const remainingPath = "/" + remainingSegments.join("/");

    return { prefixValues, remainingPath, remainingSegments };
  }

  override async connectedCallback() {
    super.connectedCallback();
    window.addEventListener("popstate", this.handlePopState);
  }

  override disconnectedCallback() {
    window.removeEventListener("popstate", this.handlePopState);
    super.disconnectedCallback();
  }

  private async setRouteFromUrl() {
    const fullPath = location.pathname;

    // 1. Extract prefix from full path (before baseUrl processing)
    const prefixResult = this.extractPrefixFromPath(fullPath);

    if (!prefixResult) {
      // Prefix pattern doesn't match - redirect to 404
      const notFoundRoute = this.get404Route();
      if (notFoundRoute) {
        await this.updateRoute(notFoundRoute, false);
      } else {
        await this.updateRoute(this.getDefaultRoute(), false);
      }
      return;
    }

    // 2. Update prefix object
    Object.assign(this.routePrefix, prefixResult.prefixValues);

    // 3. Extract route and remaining segments
    const routeSegments = prefixResult.remainingSegments;

    if (routeSegments.length === 0) {
      // No route specified, use default
      await this.updateRoute(this.getDefaultRoute(), false, []);
      return;
    }

    // Try to match the full path (or progressively shorter paths) against route names
    // This handles routes with slashes like "worlds/posts" or "sign-up/confirm-email"
    let matchedRoute: string | undefined;
    let additionalSegments: string[] = [];

    for (let i = routeSegments.length; i > 0; i--) {
      const potentialRoute = routeSegments.slice(0, i).join("/");
      if (this.config[potentialRoute as keyof TConfig]) {
        matchedRoute = potentialRoute;
        additionalSegments = routeSegments.slice(i);
        break;
      }
    }

    // If no match found, use first segment as route
    const route = matchedRoute || routeSegments[0];
    if (!matchedRoute) {
      additionalSegments = routeSegments.slice(1);
    }

    await this.updateRoute(route as keyof TConfig, false, additionalSegments);
  }

  private handlePopState(e: PopStateEvent) {
    this.historyState = e.state;
    this.setRouteFromUrl();
  }

  async navigateTo(route: keyof TConfig, updateHistory = true, additionalPathSegments: string[] = []) {
    if (isProcessingCheckout()) {
      return false;
    }

    const prevPage = location.pathname;
    const state = { self: true, prevPage };
    const res = await this.updateRoute(route, updateHistory, additionalPathSegments, state);
    if (res === true) {
      window.dispatchEvent(new PopStateEvent("popstate", { state }));
    }

    return res;
  }

  private get404Route(): keyof TConfig | undefined {
    const notFoundRoute = Object.entries(this.config).find(([, config]) => config.is404)?.[0];
    return notFoundRoute as keyof TConfig | undefined;
  }

  private getValidRoute(route: keyof TConfig): keyof TConfig {
    const routeConfig = this.config[route];
    if (routeConfig) return route;

    // For non-existent routes, use 404 page or keep invalid route (for not found message)
    const notFoundRoute = this.get404Route();
    return notFoundRoute !== undefined ? notFoundRoute : route;
  }

  private async updateRoute(
    route: keyof TConfig,
    updateHistory: boolean,
    additionalPathSegments: string[] = [],
    state?: unknown,
  ) {
    const nextRoute = route;
    const routeConfig = this.config[nextRoute];

    // Check canDeactivate on current page before navigation
    if (this.currentRoute !== nextRoute) {
      const canDeactivate = await this.checkCanDeactivate(String(route));
      if (!canDeactivate) {
        return false; // Abort navigation
      }
    }

    // Handle private routes first
    if (routeConfig?.isPrivate) {
      const defaultRoute = this.getDefaultRoute();
      if (!this.currentUser) {
        // No user logged in, redirect to default route if not already there
        if (route !== defaultRoute) {
          await this.navigateTo(defaultRoute);
          return true;
        }
      }

      // User is logged in but check role requirements
      if (this.currentUser && routeConfig.requiredRole && !this.currentUser.roles.includes(routeConfig.requiredRole)) {
        // User doesn't have the required role, redirect if not already there
        if (route !== defaultRoute) {
          await this.navigateTo(defaultRoute);
          return true;
        }
      }
    }

    // Get a valid route (original, 404, or default)
    const newRoute = this.getValidRoute(nextRoute);

    if (updateHistory) {
      // Build URL with preserved prefix
      const prefixPath =
        this.prefixSegments.length > 0
          ? "/" + this.prefixSegments.map((segment) => (this.routePrefix as Record<string, string>)[segment]).join("/")
          : "";

      const routePath = newRoute === this.getDefaultRoute() ? "" : String(newRoute);

      // Properly construct the full path avoiding double slashes
      let fullPath = prefixPath;

      if (this.baseUrl !== "/") {
        fullPath += this.baseUrl.endsWith("/") ? this.baseUrl.slice(0, -1) : this.baseUrl;
      }

      if (routePath) {
        fullPath += (fullPath.endsWith("/") ? "" : "/") + routePath;
      } else if (!fullPath.endsWith("/") && (prefixPath || this.baseUrl !== "/")) {
        // Add trailing slash for default routes when we have prefix or custom baseUrl
        fullPath += "/";
      }

      // Ensure we always have at least "/"
      if (!fullPath) {
        fullPath = "/";
      }
      if (additionalPathSegments.length > 0) {
        if (fullPath.at(-1) !== "/") {
          fullPath += "/";
        }
        fullPath += additionalPathSegments.join("/");
      }

      window.history.pushState(state, "", fullPath);
    }

    this.remainingSegments = additionalPathSegments;
    if (this.currentRoute === newRoute) {
      return true;
    }

    const newRouteConfig = this.config[newRoute];
    if (newRouteConfig) {
      document.title = newRouteConfig.title ? "WOW | " + newRouteConfig.title : "WOW | " + newRoute.toString();
    } else {
      document.title = "World Of WOW";
    }

    this.currentRoute = newRoute;
    window.scrollTo(0, 0);
    await this.updateComplete;
    return true;
  }

  /**
   * Check if the current page allows deactivation using stored page instance and config
   */
  private async checkCanDeactivate(targetRoute: string): Promise<boolean> {
    if (
      this._currentPageInstance &&
      "canDeactivate" in this._currentPageInstance &&
      this._currentPageInstance.canDeactivate != undefined
    ) {
      try {
        const result = await this._currentPageInstance.canDeactivate(this._currentPageInstance, targetRoute);
        return result !== false;
      } catch (error) {
        console.error("Error in canDeactivate:", error);
        // Allow navigation if canDeactivate throws an error
        return true;
      }
    }

    // Allow navigation if no canDeactivate method
    return true;
  }

  @property({ reflect: false, type: Object })
  currentUser: { id: string; name: string; roles: [string] } | undefined;

  /**
   * Helper method to create and store the page instance and config
   */
  private _createAndStorePage(routeKey: keyof TConfig): LitElement | null {
    const routeConfig = this.config[routeKey];

    if (!routeConfig || !routeConfig.page) {
      // Try 404 route
      const notFoundRoute = this.get404Route();
      if (notFoundRoute && this.config[notFoundRoute]) {
        const NotFoundPageCtor = this.config[notFoundRoute].page;
        this._currentPageInstance = new NotFoundPageCtor();
        this._currentRouteConfig = this.config[notFoundRoute];
        return this._currentPageInstance;
      }
      // Clear stored instances if no valid page
      this._currentPageInstance = undefined;
      this._currentRouteConfig = undefined;
      return null;
    }

    let actualRouteConfig = routeConfig;

    // Handle private route redirects
    if (!this.currentUser && routeConfig.isPrivate) {
      actualRouteConfig = this.config[this.getDefaultRoute()];
    }

    if (!actualRouteConfig || !actualRouteConfig.page) {
      this._currentPageInstance = undefined;
      this._currentRouteConfig = undefined;
      return null;
    }

    // Create new page instance and store it with its config
    const PageCtor = actualRouteConfig.page;
    if (!(this._currentPageInstance instanceof PageCtor)) {
      this._currentPageInstance = new PageCtor();
    }
    this._currentRouteConfig = actualRouteConfig;

    return this._currentPageInstance;
  }

  override render() {
    const cssClass = "page";

    // Create and store the page instance for the current route
    const pageInstance = this._createAndStorePage(this.currentRoute);

    if (!pageInstance) {
      return html`<div class=${cssClass}>Page not found</div>`;
    }

    // Determine the actual route config being used (could be redirected for private routes)
    const actualRouteConfig = this._currentRouteConfig;

    const layoutCtor =
      actualRouteConfig?.layout ??
      (actualRouteConfig && "layout" in actualRouteConfig === false ? this.defaultLayout : undefined);

    if (layoutCtor) {
      if (this.layoutInstance == undefined) {
        this.layoutInstance = new layoutCtor();
      }

      render(html`<div class=${cssClass}>${pageInstance}</div>`, this.layoutInstance);
      return this.layoutInstance;
    }

    return html`<div class=${cssClass}>${pageInstance}</div>`;
  }

  layoutInstance?: HTMLElement;
}

/**
 * Router initialization function
 *
 * @export
 * @template {Record<string, RouteConfig>} T
 * @template {string} TPrefixPattern
 * @param {T} config - Router configuration
 * @param {new() => HTMLElement} defaultLayout - Html element constructor for the default layout.
 * @param {TPrefixPattern} prefixPathPattern - Path pattern for prefix extraction
 * @returns {RouterInstance<T, TPrefixPattern>}
 */
export function initRouter<T extends Record<string, RouteConfig>, TPrefixPattern extends string = "">(
  config: T,
  defaultLayout: new () => HTMLElement,
  prefixPathPattern?: TPrefixPattern,
): RouterInstance<T, TPrefixPattern> {
  const router = new WowRouter(config, defaultLayout, prefixPathPattern || ("" as TPrefixPattern));
  return {
    // navigateTo: (route) => router.navigateTo(route),
    navigateTo: router.navigateTo.bind(router),
    router: router,
  };
}
